import React, { useRef, useState, useEffect, ReactNode } from "react";
import { cleanClassNames } from "../../../helpers/classNames";
import "../Dropdown/style.scss";
import SearchBar from "../SearchBar";
import StyledContainer from "./style";

export interface DropdownOption {
  label: ReactNode;
  value: string;
  id?: number | string;
}

interface AutocompleteProps {
  options: DropdownOption[];
  onChange: (option: DropdownOption) => void;
  selectedOption: DropdownOption | null;
  className?: string;
  placeholder?: string;
  multiple?: boolean;
  multipleSelectedLabel?: ReactNode;
  isServerSide?: boolean;
  onSearchChange?: (value: string) => void;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  options,
  onChange,
  selectedOption,
  className,
  placeholder = "Select an option",
  multiple = false,
  multipleSelectedLabel,
  isServerSide = false,
  onSearchChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLInputElement>(null);

  const filteredOptions = isServerSide
    ? options
    : options.filter((option) =>
        option.value.toLowerCase().includes(inputValue.toLowerCase()),
      );
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchBarRef.current) {
      searchBarRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option: DropdownOption) => {
    onChange(option);
    if (!multiple) {
      setIsOpen(false);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  return (
    <StyledContainer
      className={cleanClassNames("dropdown-container", className)}
      ref={containerRef}
    >
      <div className="autocomplete-input-wrapper">
        {isOpen || !selectedOption ? (
          <SearchBar
            placeholder={placeholder}
            searchedData={inputValue}
            handleSearch={(e) => {
              setInputValue(e.target.value);
              if (
                (isServerSide && e.target.value.length >= 3) ||
                !isServerSide
              ) {
                setIsOpen(true);
              }
              if (isServerSide && onSearchChange) {
                onSearchChange?.(e.target.value);
              }
            }}
            onFocus={handleFocus}
            showIcon={true}
            ref={searchBarRef}
          />
        ) : (
          <button
            className="dropdown-button"
            onClick={() => setIsOpen(true)}
            type="button"
          >
            {multiple
              ? multipleSelectedLabel || selectedOption?.label
              : selectedOption?.label}
          </button>
        )}
      </div>
      {isOpen && (
        <div className="dropdown-options">
          {filteredOptions.length > 0 ? (
            <ul>
              {filteredOptions.map((option, idx) => (
                <li
                  key={option.value}
                  className="dropdown-option"
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          ) : (
            <div className="dropdown-option empty">No results</div>
          )}
        </div>
      )}
    </StyledContainer>
  );
};

export default Autocomplete;
